import { useEffect, useState } from "react";

import { Dialog } from "../base/Dialog";
import { getTelephonyProviderInfo } from "../../utils/telephony";
import { Icon } from "../base/Icon";
import { PhoneNumber } from "../../redux/reducers/userSlice";
import { TelephonyConfigurationDropdown } from "./TelephonyConfigurationDropdown";
import { useApiRequest } from "../../hooks/useApiRequest";
import { useAppSelector } from "../../redux";
import { useEffectiveOrganization } from "../../hooks/useEffectiveOrganization";
import { View, Text, Button, Card } from "../base";

type PhoneNumberCardProps = {
  phoneNumberId?: string;
  className?: string;
};

export const PhoneNumberCard = ({ phoneNumberId, className }: PhoneNumberCardProps) => {
  const [showProviderDialog, setShowProviderDialog] = useState(false);
  const [selectedTelephonyId, setSelectedTelephonyId] = useState("");
  const getPhoneNumberApiRequest = useApiRequest<{ data: PhoneNumber }>();
  const createPhoneNumberApiRequest = useApiRequest<{ data: PhoneNumber }>();

  // Get telephony configurations from Redux store
  const { organizationId: effectiveOrganizationId } = useEffectiveOrganization();
  const telephonyConfigs = useAppSelector((state) =>
    effectiveOrganizationId ? Object.values(state.organizationTelephony.entities).filter((config) => config.organizationId === effectiveOrganizationId) : [],
  );

  const handleIssuePhoneNumber = async () => {
    try {
      await createPhoneNumberApiRequest.fetch("/v1/users/phoneNumber", {
        method: "POST",
        body: {
          telephonyId: selectedTelephonyId,
        },
      });

      await getPhoneNumberApiRequest.fetch("/v1/users/phoneNumber");

      setShowProviderDialog(false);
      setSelectedTelephonyId("");
    } catch (error) {
      console.error("Failed to issue phone number:", error);
    }
  };

  useEffect(() => {
    getPhoneNumberApiRequest.fetch("/v1/users/phoneNumber");
  }, []);

  const phoneNumber = getPhoneNumberApiRequest.data?.data.phoneNumber;
  const phoneNumberData = getPhoneNumberApiRequest.data?.data;

  // Find the telephony configuration for the current phone number
  const telephonyConfig = phoneNumberData?.telephonyId ? telephonyConfigs.find((config) => config.id === phoneNumberData.telephonyId) : null;
  const providerInfo = telephonyConfig ? getTelephonyProviderInfo(telephonyConfig.providerType) : getTelephonyProviderInfo("");

  return (
    <Card className={`p-6 relative ${className || ""}`}>
      <View className="sm:pr-48">
        <Text className="text-lg font-semibold mb-4">Phone Number</Text>
        <View className="flex flex-col gap-2">
          {phoneNumber ? (
            <View className="flex flex-col gap-3">
              <Text
                variant="muted"
                className="text-sm"
              >
                This number is currently assigned to your user. You can use it for debugging your agents.
              </Text>
              <Text className="text-lg">{phoneNumber}</Text>

              {/* Telephony Configuration Display */}
              <View className="flex flex-col gap-2 p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg border border-gray-200 dark:border-gray-600">
                <Text
                  variant="muted"
                  className="text-sm font-medium"
                >
                  Telephony Configuration
                </Text>
                <View className="flex flex-row items-center gap-2">
                  <Icon
                    name={providerInfo.icon}
                    size={16}
                    className="text-gray-700 dark:text-gray-300"
                  />
                  <Text className="text-sm">{telephonyConfig ? telephonyConfig.name : providerInfo.name}</Text>
                </View>
              </View>
            </View>
          ) : (
            <View className="flex flex-col gap-1">
              <Text
                variant="muted"
                className="text-sm"
              >
                Issue a phone number to enable voice calling capabilities for your agents.
              </Text>
              <Text className="text-lg">No phone number assigned</Text>
            </View>
          )}
        </View>
      </View>
      {(!phoneNumberId || !phoneNumber) && (
        <View className="mt-4 sm:mt-0 sm:absolute sm:right-6 sm:top-1/2 sm:-translate-y-1/2">
          <Button
            onClick={() => setShowProviderDialog(true)}
            variant="brand"
            className="w-full sm:w-auto"
          >
            Issue Phone Number
          </Button>
        </View>
      )}

      <Dialog
        isOpen={showProviderDialog}
        onClose={() => {
          setShowProviderDialog(false);
          setSelectedTelephonyId("");
        }}
        title="Issue Phone Number"
      >
        <View className="flex flex-col gap-6 p-6">
          <View className="flex flex-col gap-2">
            <Text
              variant="muted"
              className="text-sm"
            >
              Select a telephony configuration to issue a phone number. You can choose VoiceRun or one of your configured providers.
            </Text>
          </View>

          <TelephonyConfigurationDropdown
            value={selectedTelephonyId}
            onChange={setSelectedTelephonyId}
            organizationId={effectiveOrganizationId}
            placeholder="Select a telephony configuration"
          />

          <View className="flex flex-row gap-3 mt-2">
            <Button
              variant="brand"
              onClick={handleIssuePhoneNumber}
              className="flex-1"
              isLoading={createPhoneNumberApiRequest.isLoading}
            >
              Issue Number
            </Button>
            <Button
              variant="ghost"
              onClick={() => {
                setShowProviderDialog(false);
                setSelectedTelephonyId("");
              }}
              className="flex-1"
            >
              Cancel
            </Button>
          </View>
        </View>
      </Dialog>
    </Card>
  );
};
